import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartAddPendingEvent } from '@theme/events';
import { isMobileBreakpoint, isBackForwardNavigation } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** Whether this open cycle pushed a mobile history entry. */
  #didPushHistoryEntry = false;

  /** True while closing from popstate so close does not call history.back() again. */
  #closingFromPopState = false;

  /** Reopen after a close animation if an add started while closing. */
  #reopenAfterClose = false;

  /** Belt: ignore openForAdd briefly after pageshow/back_forward (see native-cart-drawer.md). */
  #suppressAutoOpenUntil = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartAddPendingEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#focusDialog);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#reopenAfterForcedClose);
    window.addEventListener('pageshow', this.#handlePageShow);

    if (history.state?.cartDrawerOpen) {
      this.#clearCartDrawerHistoryState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartAddPendingEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#focusDialog);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#reopenAfterForcedClose);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#teardownHistory();
  }

  /**
   * Mobile open pushes a same-URL history entry so Back closes the drawer.
   * Abort any prior listener before attaching a new one.
   */
  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;
    if (this.#historyAbortController) return;

    this.#didPushHistoryEntry = true;
    history.pushState({ ...history.state, cartDrawerOpen: true }, '');

    this.#historyAbortController = new AbortController();
    const { signal } = this.#historyAbortController;
    window.addEventListener('popstate', this.#handlePopState, { signal });
    this.addEventListener('click', this.#handleDrawerLinkClick, { capture: true, signal });
  };

  #handleHistoryClose = () => {
    if (!this.#historyAbortController && !this.#didPushHistoryEntry) return;

    const shouldGoBack = this.#didPushHistoryEntry && !this.#closingFromPopState;
    this.#teardownHistory();

    if (shouldGoBack) {
      history.back();
    }
  };

  #handlePopState = async () => {
    this.#didPushHistoryEntry = false;
    this.#closingFromPopState = true;

    try {
      if (this.refs.dialog?.open) {
        this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
        await this.closeDialog();
        this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
      } else {
        this.#teardownHistory();
        this.#clearCartDrawerHistoryState();
      }
    } finally {
      this.#closingFromPopState = false;
    }
  };

  /**
   * In-drawer links (Continue shopping, Log in, upsell PDP) navigate without
   * closeDialog. Clear the cart history entry and unlock so bfcache cannot
   * freeze an open modal snapshot.
   *
   * @param {MouseEvent} event
   */
  #handleDrawerLinkClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (!this.refs.dialog?.contains(anchor)) return;

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

    this.#prepareLeaveViaNavigation();
  };

  /**
   * Drop the cart history flag and force-close before a full navigation so the
   * bfcached page is not restored with an open dialog + body scroll lock.
   */
  #prepareLeaveViaNavigation() {
    this.#didPushHistoryEntry = false;
    this.#teardownHistory();
    this.#clearCartDrawerHistoryState();
    this.#forceResetDrawerChrome();
  }

  /**
   * bfcache / back_forward can restore an open dialog that never ran closeDialog.
   * Force-reset chrome and clear cartDrawerOpen without another history.back().
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const isBackForward = isBackForwardNavigation();

    if (!event.persisted && !isBackForward) return;

    this.#suppressAutoOpenUntil = Date.now() + 1500;

    const dialog = this.refs.dialog ?? this.querySelector('dialog');
    const dialogOpen = dialog instanceof HTMLDialogElement && dialog.open;
    const hasCartHistory = Boolean(history.state?.cartDrawerOpen);

    if (!dialogOpen && !hasCartHistory) return;

    this.#didPushHistoryEntry = false;
    this.#teardownHistory();
    this.#clearCartDrawerHistoryState();

    if (dialogOpen) {
      this.#closingFromPopState = true;
      dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      this.closeDialog()
        .catch(() => {
          this.#forceResetDrawerChrome();
        })
        .finally(() => {
          dialog.style.removeProperty('--dialog-drawer-closing-animation');
          this.#closingFromPopState = false;
        });
      return;
    }

    this.#forceResetDrawerChrome();
  };

  #teardownHistory() {
    this.#historyAbortController?.abort();
    this.#historyAbortController = null;
    this.#didPushHistoryEntry = false;
  }

  #clearCartDrawerHistoryState() {
    const state = history.state;
    if (!state || typeof state !== 'object' || !('cartDrawerOpen' in state)) return;

    const nextState = { ...state };
    delete nextState.cartDrawerOpen;
    history.replaceState(Object.keys(nextState).length ? nextState : null, '');
  }

  /**
   * Immediately dismiss modal chrome without animation or history.back().
   * Used for bfcache restore and in-drawer link navigation.
   */
  #forceResetDrawerChrome() {
    const dialog = this.refs.dialog ?? this.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.style.removeProperty('--dialog-drawer-closing-animation');
      dialog.classList.remove('dialog-closing');
      if (dialog.open) dialog.close();
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
  }

  /**
   * showModal() focuses the close button; Safari treats that as :focus-visible.
   * Keep focus in the dialog without painting the close ring until the user tabs.
   */
  #focusDialog = () => {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    dialog.tabIndex = -1;
    dialog.focus({ preventScroll: true });
  };

  /**
   * Auto-open on pending or CartAddEvent only — not CartUpdateEvent (same `cart:update` name).
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    if (event.type === CartAddPendingEvent.eventName) {
      this.openForAdd();
      return;
    }

    if (!(event instanceof CartAddEvent)) return;
    if (event.detail?.data?.source === 'cart-icon-pageshow') return;
    // Failed / no-op adds emit cartUnchanged so chrome can clear without mutating —
    // do not auto-open (cart-icon.js already bails on the same flag).
    if (event.detail?.data?.cartUnchanged) return;

    this.openForAdd();
  };

  /**
   * Open via showDialog() (rAF). Calling showModal() in the same click that
   * added to cart lets the mouseup hit the backdrop and close the drawer.
   */
  openForAdd() {
    if (!this.hasAttribute('auto-open')) return;
    if (Date.now() < this.#suppressAutoOpenUntil) return;

    this.removeAttribute('hidden');
    const dialog = this.refs.dialog ?? this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (dialog.classList.contains('dialog-closing')) {
      this.#reopenAfterClose = true;
      return;
    }

    if (dialog.open) return;
    this.showDialog();
  }

  #reopenAfterForcedClose = () => {
    if (!this.#reopenAfterClose) return;
    this.#reopenAfterClose = false;
    queueMicrotask(() => this.openForAdd());
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  async close() {
    await this.closeDialog();

    const dialog = this.refs.dialog ?? this.querySelector('dialog');
    const stillOpen = dialog instanceof HTMLDialogElement && (dialog.open || dialog.classList.contains('dialog-closing'));
    const bodyLocked = document.body.style.position === 'fixed';

    if (!stillOpen && !bodyLocked) return;

    // closeDialog no-op'd or left chrome stuck (bfcache / #isClosing race).
    // Force-clear without history.back() so X cannot trap the user.
    this.#didPushHistoryEntry = false;
    this.#teardownHistory();
    this.#clearCartDrawerHistoryState();
    this.#forceResetDrawerChrome();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
